import React, { useEffect, useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Grid, 
  CircularProgress, 
  Typography,
  Paper,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  Divider,
  Tooltip,
  Fade,
  Chip
} from '@mui/material';
import { 
  ListFilter, 
  ChevronUp, 
  X,
  RefreshCcw,
  Car,
  UserRound,
  Search,
  AlertCircle
} from 'lucide-react';
import CarSearchResult from './CarSearchResult';
import ClientSearchResult from './ClientSearchResult';
import SearchFilters from './SearchFilters';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_DOMAIN}/car_stock/api`;
const CRM_API_URL = `${process.env.REACT_APP_DOMAIN}/crm/api`;

const SearchPanel = ({ 
  searchTab,
  setSearchTab,
  carSearchTerm,
  setCarSearchTerm,
  showFilters,
  setShowFilters,
  clientSearchTerm,
  setClientSearchTerm,
  searchResults,
  setSearchResults,
  clientSearchResults,
  setClientSearchResults,
  searchLoading,
  setSearchLoading,
  clientSearchLoading,
  setClientSearchLoading,
  isFullscreen,
  handleAddCarKnowledge,
  handleAddClientKnowledge,
  darkMode,
  filters,
  setFilters,
  filterOptions,
  setFilterOptions,
  onClose,
  isMobile,
  showCloseButton = false,
}) => {

  // Add useEffect to handle body scroll
  useEffect(() => {
    if (isMobile) {
      // Prevent background scroll when panel is open
      document.body.style.overflow = 'hidden';
      
      // Cleanup when component unmounts
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
  }, [isMobile]);

  // Fetch car data
  const fetchCarData = async (term) => {
    if (!term && !Object.values(filters).some(value => value !== '')) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await axios.get(`${API_BASE_URL}/carstocks/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          search: term,
          brand: filters.brand || undefined,
          min_price: filters.min_price || undefined,
          max_price: filters.max_price || undefined,
          min_year: filters.min_year || undefined,
          max_year: filters.max_year || undefined,
          min_km: filters.min_km || undefined,
          max_km: filters.max_km || undefined,
          fuel: filters.fuel || undefined,
        }
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Error fetching car data:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Fetch client data
  const fetchClientData = async (term) => {
    if (!term.trim()) {
      setClientSearchResults([]);
      return;
    }

    try {
      setClientSearchLoading(true);
      const response = await axios.get(`${CRM_API_URL}/Clients/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          search: term,
          page_size: 10
        }
      });
      
      const clients = response.data.results || [];
      setClientSearchResults(clients.map(client => ({
        id: client.id,
        name: `${client.first_name} ${client.last_name}`.trim(),
        email: client.email,
        phone: client.number,
        last_contact: client.last_contact,
        first_name: client.first_name,
        last_name: client.last_name,
        number: client.number
      })));
    } catch (error) {
      console.error('Error fetching client data:', error);
      setClientSearchResults([]);
    } finally {
      setClientSearchLoading(false);
    }
  };

  // Fetch filter options when component mounts
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/filter-options/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        setFilterOptions(response.data);
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    
    fetchFilterOptions();
  }, [setFilterOptions]);

  // Update search when car search term or filters change
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCarData(carSearchTerm);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [carSearchTerm, filters]);

  // Update search when client search term changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchClientData(clientSearchTerm);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [clientSearchTerm]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Count the active filters
  const activeFiltersCount = Object.values(filters).filter(value => value !== '').length;

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      ...(isMobile && {
        zIndex: 1000,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden'
      })
    }}>
      {/* Header with Title */}
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        position: 'sticky',
        top: 0,
        zIndex: 1300,
        bgcolor: darkMode ? '#1E1E1E' : '#fff',
        py: 1,
        px: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          {isMobile ? 'Search' : 'Knowledge Base'}
        </Typography>
        
        {(isMobile || showCloseButton) && (
          <IconButton 
            onClick={onClose}
            aria-label="Close search panel"
            sx={{ 
              color: darkMode ? '#fff' : '#333',
            }}
          >
            <X size={20} />
          </IconButton>
        )}
      </Box>

      {/* Tabs and Search Content */}
      <Paper sx={{ 
        mx: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        bgcolor: darkMode ? '#2A2A2A' : '#fff',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: darkMode ? '#333' : '#f8f9fa',
        }}>
          <Tabs
            value={searchTab}
            onChange={(e, newValue) => {
              setSearchTab(newValue);
              setShowFilters(false);
            }}
            variant="fullWidth"
            sx={{ 
              minHeight: '48px',
              '& .MuiTab-root': {
                minHeight: '48px',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.95rem',
              },
              width: '100%',
            }}
            TabIndicatorProps={{
              style: {
                height: 3,
                borderTopLeftRadius: 3,
                borderTopRightRadius: 3,
              }
            }}
          >
            <Tab 
              value="cars" 
              label="Cars" 
              icon={<Car size={18} />} 
              iconPosition="start" 
              aria-label="Search cars"
            />
            <Tab 
              value="clients" 
              label="Clients" 
              icon={<UserRound size={18} />} 
              iconPosition="start" 
              aria-label="Search clients"
            />
          </Tabs>
        </Box>

        <Box sx={{ 
          px: 2, 
          pb: 2,
        }}>
          {searchTab === 'cars' ? (
            <Box>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 2,
                width: '100%'
              }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search cars by brand, model, year..."
                  value={carSearchTerm}
                  onChange={(e) => setCarSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} color={darkMode ? "#aaa" : "#666"} />
                      </InputAdornment>
                    ),
                    endAdornment: carSearchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setCarSearchTerm('')}
                          edge="end"
                          aria-label="Clear search"
                        >
                          <X size={16} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 1.5,
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 2px rgba(67, 97, 238, 0.25)',
                      }
                    }
                  }}
                />
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  width: '100%',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                }}>
                  <Button
                    fullWidth={isMobile}
                    variant={showFilters ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setShowFilters(!showFilters)}
                    startIcon={<ListFilter size={16} />}
                    endIcon={activeFiltersCount > 0 && !showFilters ? 
                      <Chip 
                        label={activeFiltersCount} 
                        size="small" 
                        sx={{ 
                          height: 20, 
                          minWidth: 20,
                          fontSize: '0.75rem',
                          bgcolor: 'primary.main',
                          color: 'white',
                        }} 
                      /> : <ChevronUp 
                        style={{ 
                          transform: showFilters ? 'rotate(0deg)' : 'rotate(180deg)',
                          transition: 'transform 0.2s ease-in-out'
                        }} 
                        size={16} 
                      />
                    }
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      flexGrow: 1,
                      minWidth: 120,
                      '&:hover': {
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    {showFilters ? 'Hide Filters' : 'Filters'}
                  </Button>
                  
                  <Button
                    fullWidth={isMobile}
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={() => {
                      setCarSearchTerm('');
                      setFilters({
                        min_price: '',
                        max_price: '',
                        min_year: '',
                        max_year: '',
                        min_km: '',
                        max_km: '',
                        brand: '',
                        model: '',
                        fuel: '',
                        bodystyle: '',
                        transmission: '',
                        color: '',
                      });
                    }}
                    startIcon={<RefreshCcw size={16} />}
                    disabled={!carSearchTerm && activeFiltersCount === 0}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      minWidth: 100,
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            <TextField
              fullWidth
              size="small"
              placeholder="Search clients by name, email, phone..."
              value={clientSearchTerm}
              onChange={(e) => setClientSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={darkMode ? "#aaa" : "#666"} />
                  </InputAdornment>
                ),
                endAdornment: clientSearchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setClientSearchTerm('')}
                      edge="end"
                      aria-label="Clear search"
                    >
                      <X size={16} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 1.5,
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 2px rgba(67, 97, 238, 0.25)',
                  }
                }
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Filters */}
      {searchTab === 'cars' && showFilters && (
        <Fade in={showFilters}>
          <Box sx={{ px: 2 }}>
            <SearchFilters 
              filters={filters}
              filterOptions={filterOptions}
              handleFilterChange={handleFilterChange}
              isFullscreen={isFullscreen}
              darkMode={darkMode}
              isMobile={isMobile}
            />
          </Box>
        </Fade>
      )}

      {/* Results Container */}
      <Box sx={{ 
        mt: 2,
        flex: 1,
        overflowY: 'auto',
        height: showFilters ? 'calc(100% - 280px)' : 'calc(100% - 180px)',
        px: 2,
        pb: 2,
        ...(isMobile && {
          WebkitOverflowScrolling: 'touch',
          position: 'relative',
          zIndex: 1100
        })
      }}>
        {/* Car Results */}
        {searchTab === 'cars' && (
          <>
            {/* Results Status Section */}
            {!searchLoading && searchResults.length > 0 && (
              <Box sx={{ 
                mb: 2, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="body2" color="text.secondary">
                  {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} found
                </Typography>
              </Box>
            )}

            <Grid container spacing={2.5}>
              {searchResults.map((car) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={isFullscreen ? 6 : 12} 
                  md={isFullscreen ? 6 : 12} 
                  lg={isFullscreen ? 4 : 12} 
                  xl={isFullscreen ? 3 : 12} 
                  key={car.vehicleid || car.id}
                >
                  <CarSearchResult 
                    car={car} 
                    isFullscreen={isFullscreen} 
                    onAddKnowledge={handleAddCarKnowledge}
                    darkMode={darkMode}
                    isMobile={isMobile}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Empty State */}
            {!searchLoading && searchResults.length === 0 && (carSearchTerm || activeFiltersCount > 0) && (
              <Box 
                sx={{ 
                  py: 8, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 2,
                  color: darkMode ? '#aaa' : '#666',
                }}
              >
                <AlertCircle size={32} />
                <Typography variant="h6">No cars found</Typography>
                <Typography variant="body2" sx={{ maxWidth: 400 }}>
                  Try adjusting your search term or filters to find what you're looking for.
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary"
                  size="small"
                  startIcon={<RefreshCcw size={16} />}
                  onClick={() => {
                    setCarSearchTerm('');
                    setFilters({
                      min_price: '',
                      max_price: '',
                      min_year: '',
                      max_year: '',
                      min_km: '',
                      max_km: '',
                      brand: '',
                      model: '',
                      fuel: '',
                      bodystyle: '',
                      transmission: '',
                      color: '',
                    });
                  }}
                  sx={{ mt: 2, borderRadius: 1.5, textTransform: 'none' }}
                >
                  Reset Search
                </Button>
              </Box>
            )}
            
            {/* Initial State */}
            {!searchLoading && searchResults.length === 0 && !carSearchTerm && activeFiltersCount === 0 && (
              <Box 
                sx={{ 
                  py: 8, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 2,
                  color: darkMode ? '#aaa' : '#666',
                }}
              >
                <Car size={32} />
                <Typography variant="h6">Search for cars</Typography>
                <Typography variant="body2" sx={{ maxWidth: 400 }}>
                  Enter a search term or use filters to find cars.
                </Typography>
              </Box>
            )}
            
            {/* Loading State */}
            {searchLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress size={40} />
              </Box>
            )}
          </>
        )}

        {/* Client Results */}
        {searchTab === 'clients' && (
          <Box>
            {!clientSearchLoading && clientSearchResults.length > 0 && (
              <Box sx={{ 
                mb: 2, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="body2" color="text.secondary">
                  {clientSearchResults.length} {clientSearchResults.length === 1 ? 'client' : 'clients'} found
                </Typography>
              </Box>
            )}

            <Grid container spacing={2}>
              {clientSearchResults.map((client) => (
                <Grid item xs={12} key={client.id}>
                  <ClientSearchResult 
                    client={client}
                    onAddKnowledge={handleAddClientKnowledge}
                    darkMode={darkMode}
                    isMobile={isMobile}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Empty Search State */}
            {!clientSearchLoading && clientSearchResults.length === 0 && clientSearchTerm && (
              <Box 
                sx={{ 
                  py: 8, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 2,
                  color: darkMode ? '#aaa' : '#666',
                }}
              >
                <AlertCircle size={32} />
                <Typography variant="h6">No clients found</Typography>
                <Typography variant="body2" sx={{ maxWidth: 400 }}>
                  Try adjusting your search term to find what you're looking for.
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary"
                  size="small"
                  startIcon={<RefreshCcw size={16} />}
                  onClick={() => setClientSearchTerm('')}
                  sx={{ mt: 2, borderRadius: 1.5, textTransform: 'none' }}
                >
                  Reset Search
                </Button>
              </Box>
            )}
            
            {/* Initial State */}
            {!clientSearchLoading && clientSearchResults.length === 0 && !clientSearchTerm && (
              <Box 
                sx={{ 
                  py: 8, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 2,
                  color: darkMode ? '#aaa' : '#666',
                }}
              >
                <UserRound size={32} />
                <Typography variant="h6">Search for clients</Typography>
                <Typography variant="body2" sx={{ maxWidth: 400 }}>
                  Enter a search term to find clients.
                </Typography>
              </Box>
            )}
            
            {/* Loading State */}
            {clientSearchLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress size={40} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SearchPanel; 